import React from "react";
import { Check } from "lucide-react";

/**
 * PlansCard
 *
 * Read-only plan comparison.
 * No automatic payments — upgrade happens manually via admin verification.
 *
 * Props:
 * - plans: [
 *     {
 *       id: string
 *       name: string
 *       priceLabel: string
 *       description?: string
 *       features: string[]
 *       isCurrent?: boolean
 *     }
 *   ]
 */
export default function PlansCard({ plans = [] }) {
  if (!plans.length) return null;

  return (
    <div className="rounded-xl border bg-white p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Available Plans
        </h2>
        <p className="text-sm text-gray-500">
          Billing is currently handled manually. Contact support to upgrade.
        </p>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`rounded-xl border p-5 space-y-4 ${
              plan.isCurrent
                ? "border-indigo-500 bg-indigo-50"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* Plan header */}
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {plan.name}
                </h3>

                {plan.isCurrent && (
                  <span className="text-xs font-medium text-indigo-600">
                    Current Plan
                  </span>
                )}
              </div>

              <div className="mt-1 text-sm text-gray-500">
                {plan.priceLabel}
              </div>
            </div>

            {/* Description */}
            {plan.description && (
              <p className="text-sm text-gray-600">
                {plan.description}
              </p>
            )}

            {/* Features */}
            <ul className="space-y-2 text-sm">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Manual upgrade note */}
            {!plan.isCurrent && (
              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                To upgrade to this plan, complete payment offline and
                contact support for activation.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}